import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreHttpClient } from '@bepatient/core';
import * as moment from 'moment';
import { concat, Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  Discussion,
  DiscussionMessage,
  DiscussionMessageToSend,
  DiscussionOverview,
  Discussions,
  MessagesContact
} from './messages.models';

@Injectable()
export class MessagesApiService {
  private http = inject(CoreHttpClient);

  downloadFile(url: string): Observable<HttpResponse<Blob>> {
    // concat is used to make sure the blob is completed
    return concat(this.http.downloadBlob(url, { withMobiCredentials: true }));
  }

  openFile(_blob: Blob): Observable<any> {
    return of();
    // return from(
    //   this.file.writeFile(
    //     this.file.dataDirectory,
    //     this.generateFilename(blob.type),
    //     blob
    //   )
    // ).pipe(
    //   map(({ nativeURL }) => from(this.fileOpener.open(nativeURL, blob.type)))
    // );
  }

  loadDiscussions(): Observable<Discussions> {
    return this.http
      .get<any>('/api/discussions', {
        withMobiCredentials: true
      })
      .pipe(map<any, Discussions>(res => this.discussionsParser(res)));
  }

  loadDiscussion(discussionUuid: string): Observable<Discussion> {
    return this.http
      .get<any>(`/api/discussions/${discussionUuid}`, {
        withMobiCredentials: true
      })
      .pipe(map<any, Discussion>(res => this.discussionParser(res)));
  }

  sendMessage(
    discussionUuid: string,
    messageToSend: DiscussionMessageToSend
  ): Observable<DiscussionMessage> {
    return of(messageToSend).pipe(
      switchMap(message =>
        message.message || message.attachment
          ? this.http
              .post<{}>(
                `/api/discussions/${discussionUuid}`,
                {
                  message_type: message.attachment ? 'attachment' : 'message',
                  message: '',
                  ...message
                },
                { withMobiCredentials: true }
              )
              .pipe(
                map<{}, DiscussionMessage>(response =>
                  this.sentMessageParser(response)
                )
              )
          : throwError('Message is empty')
      )
    );
  }

  loadContacts(): Observable<Array<MessagesContact>> {
    return this.http
      .get<any>('/api/users/me/contacts', { withMobiCredentials: true })
      .pipe(map<any, Array<MessagesContact>>(res => this.contactsParser(res)));
  }

  createDiscussion(contactUuid: string): Observable<string> {
    return this.http
      .post<any>(
        `/api/discussions`,
        { contact: contactUuid },
        { withMobiCredentials: true }
      )
      .pipe(map<any, string>(res => this.createDiscussionParser(res)));
  }

  private discussionsParser(data: any): Discussions {
    const discussions: DiscussionOverview[] = (data.items || [])
      .map(item => {
        const meta = item.meta || {};
        return <DiscussionOverview>{
          uuid: item.uuid || '',
          time: (meta.last_message && meta.last_message.ctime) || '',
          name: (meta.contact && meta.contact.nickname) || '',
          unread: meta.unread_nb > 0 || false,
          message: (meta.last_message && meta.last_message.message) || ''
        };
      })
      .sort(
        (a: DiscussionOverview, b: DiscussionOverview) =>
          moment(b.time === '' ? '0' : b.time).valueOf() -
          moment(a.time === '' ? '0' : a.time).valueOf()
      );
    return {
      discussions,
      atLeastOneUnread: !discussions.every(discussion => !discussion.unread)
    };
  }

  private discussionParser(data: any): Discussion {
    const meta = data.meta || {};
    const messages: DiscussionMessage[] = (data.items || [])
      .map(item => {
        const itemMeta = item.meta || {};
        return <DiscussionMessage>{
          time: itemMeta.ctime || '',
          isMine: (itemMeta.owner && itemMeta.owner.is_myself) || false,
          message: itemMeta.message || '',
          attachments: (itemMeta.attachments || []).map(attachment => ({
            href: attachment.abspath || '',
            name: attachment.filename || ''
          }))
        };
      })
      .sort(
        (a: DiscussionMessage, b: DiscussionMessage) =>
          moment(a.time).valueOf() - moment(b.time).valueOf()
      );

    return {
      messages,
      uuid: meta.uuid || '',
      name: (meta.contact && meta.contact.nickname) || '',
      isPro: (meta.contact && meta.contact.is_pro) || false,
      amIMuted: meta.am_i_muted || false,
      isMyContact: (meta.contact && meta.contact.is_my_contact) || false
    };
  }

  // TODO: here parse the href send by MOBI
  private sentMessageParser(response: any): DiscussionMessage {
    return {
      time: response.ctime || '',
      isMine: (response.owner && response.owner.is_myself) || true,
      message: response.message || '',
      attachments: (response.attachments || []).map(attachment => ({
        href: attachment.abspath || '',
        name: attachment.filename || ''
      }))
    };
  }

  private contactsParser(data: any): Array<MessagesContact> {
    const allowedContacts = (data.items || []).filter(
      contact =>
        contact.links && contact.links.self && contact.links.self.method
    );

    return allowedContacts
      .map(
        item =>
          <MessagesContact>{
            uuid: item.uuid || '',
            name: (item.meta && item.meta.title) || '',
            conversationHref:
              item.links && item.links.self && item.links.self.method === 'GET'
                ? item.links.self.href || ''
                : void 0
          }
      )
      .sort((a: MessagesContact, b: MessagesContact) =>
        a.name.localeCompare(b.name)
      );
  }

  private createDiscussionParser(data: any): string {
    return data.location || '';
  }

  // TODO: should be imported from common
  private generateFilename(blobType: string): string {
    let extension = null;
    let prefix = null;
    switch (blobType) {
      case 'image/jpeg':
        prefix = 'img';
        extension = '.jpg';
        break;

      case 'image/png':
        prefix = 'img';
        extension = '.png';
        break;

      case 'image/svg+xml':
        prefix = 'svg';
        extension = '.svg';
        break;

      case 'application/pdf':
        prefix = 'doc';
        extension = '.pdf';
        break;

      case 'application/vnd.ms-excel':
        prefix = 'excel';
        extension = '.xls';
        break;

      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        prefix = 'excel-OpenXML';
        extension = '.xlsx';
        break;

      case 'application/msword':
        prefix = 'word';
        extension = '.doc';
        break;

      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        prefix = 'word-OpenXML';
        extension = '.docx';
        break;
    }
    const now = new Date();
    const date = `${now.getDate()}-${now.getMonth()}-${now.getFullYear()} ${now.getHours()} ${now.getMinutes()} ${now.getSeconds()}`;

    return `${prefix} - ${date}${extension}`;
  }
}
